/**
 * HealthcareImportModal: bulk-upload a pharmacy's catalogue from a workbook.
 * ───────────────────────────────────────────────────────────────────────────
 * The four steps, the review table, the progress bar, the run log and the
 * never-close-mid-write rule are CatalogueImportModal, shared with the brand
 * importer. What is left here is what only a pharmacy has: the healthcare
 * endpoints, and a review column showing whether a row is a Product or a
 * Service, because filling Count in Stock on a consultation is the commonest
 * mistake on this sheet and the review table is the last place to catch it.
 *
 * Mounted from views/HealthcareVendorMenu.js, the page a pharmacy manages its
 * sections and products from.
 *
 * EVERY AUTHENTICATED CALL GOES THROUGH src/api, not bare axios. The
 * configured client carries the Bearer token and the active-country header
 * and refreshes an expired session; a bare axios call gets neither and
 * surfaces as an unexplained "Missing Authorization Token". That is the rule
 * src/api states on its own client, and every healthcare write below obeys
 * it.
 *
 * TWO DELIBERATE EXCEPTIONS, both in `loadExtras`: the reference-data reads
 * `fetch("/category/get-all")` and `fetch("/unit")`. This header used to say
 * EVERY call, which the two lines below then contradicted, and a rule a
 * reader can see being broken on the same screen is a rule they stop
 * believing. They are safe and they stay:
 *
 *   - Neither route is authenticated. `GET /category/get-all` and `GET /unit`
 *     are mounted with no `auth` middleware, so the Bearer token the
 *     configured client would attach buys nothing, and there is no session
 *     for it to refresh. The rule protects authenticated calls; these are not
 *     among them.
 *   - They are copied verbatim from BrandImportModal, which loads the same
 *     two lists the same way. The two importers are meant to be readable side
 *     by side, and one of them quietly reaching a different host for the same
 *     reference list is a difference somebody has to chase down later.
 *
 * The transports are also not interchangeable, which is the part worth
 * knowing before "fixing" this. `src/api` is based at REACT_APP_SERVER_URL;
 * a bare relative fetch is same-origin, which in development is the CRA
 * proxy. Moving these two reads onto the configured client changes which
 * host answers them in development. That is a behaviour change wearing a
 * tidy-up's clothes, and if it is ever wanted it belongs in both importers
 * at once, not in this one alone.
 */
import React, { useCallback } from "react";

import {
  apiCreateHealthcareProduct,
  apiCreateHealthcareSection,
  apiFetchHealthcareSections,
} from "../../api";
import { buildImportPlan } from "../../utilities/healthcareImport.js";
import { runHealthcareImport } from "../../utilities/healthcareImportRunner.js";
import { downloadTemplateWorkbook } from "../../utilities/healthcareImportTemplate.js";
import { uploadImageToCloudinary } from "../../utilities/imageUpload.js";
import CatalogueImportModal from "./CatalogueImportModal.js";

/** Izi's standard action-button colour for the dashboard. */
const ACCENT = "#083860";

/**
 * Product or Service, on the review row.
 *
 * Worth a column of its own rather than being folded into the status text: the
 * parser cannot tell a mislabelled consultation from a real product, so the
 * only defence is showing the operator what each row was read as while they
 * can still fix the sheet.
 */
const TYPE_COLUMN = {
  header: "Type",
  width: 84,
  cell: (it) => (it.productType === "SERVICE" ? "Service" : "Product"),
};

const HealthcareImportModal = ({
  show,
  vendor,
  existingSections,
  onClose,
  onImported,
}) => {
  /* Memoised because the shared modal loads it from an effect keyed on the
     function itself; a fresh closure every render would re-fetch forever. */
  const loadExtras = useCallback(async () => {
    /* The two bare fetches are the exceptions named in this file's header:
       both routes are unauthenticated, and both are the brand importer's own
       lines. Read that note before moving them onto src/api. */
    const [categories, units] = await Promise.all([
      fetch("/category/get-all")
        .then((r) => r.json())
        .catch(() => []),
      fetch("/unit")
        .then((r) => r.json())
        .catch(() => []),
    ]);
    return {
      platformCategories: Array.isArray(categories) ? categories : [],
      units: Array.isArray(units) ? units : [],
    };
  }, []);

  return (
    <CatalogueImportModal
      show={show}
      vendor={vendor}
      existingSections={existingSections}
      onClose={onClose}
      onImported={onImported}
      accent={ACCENT}
      vendorNoun="pharmacy"
      title={`Bulk import products and services for ${vendor?.name || "this pharmacy"}`}
      buildPlan={buildImportPlan}
      runImport={runHealthcareImport}
      loadExtras={loadExtras}
      templateLabel={`Download the template for ${vendor?.name || "this pharmacy"}`}
      onDownloadTemplate={({ extras, existingSections: sections }) =>
        downloadTemplateWorkbook({
          vendorName: vendor?.name || "",
          sectionTitles: (sections || []).map((s) => s.title).filter(Boolean),
          categoryNames: (extras.platformCategories || [])
            .map((c) => c.category)
            .filter(Boolean),
          unitNames: (extras.units || []).map((u) => u.unit).filter(Boolean),
        })
      }
      api={{
        /* The healthcare endpoints are flat: the vendor and the section ride
           in the body, not the path. The shared runner still hands both in,
           and the body builder puts them where this API wants them. */
        createSection: (vendorId, body) =>
          apiCreateHealthcareSection({ ...body, healthcareId: vendorId }, vendorId),
        fetchSections: (vendorId) => apiFetchHealthcareSections(vendorId),
        createProduct: (vendorId, sectionId, body) => apiCreateHealthcareProduct(body),
        uploadImage: uploadImageToCloudinary,
      }}
      extraColumns={[TYPE_COLUMN]}
      pickHelp={
        <>
          Fill the <strong>1. Sections</strong> and <strong>2. Products</strong> sheets,
          then upload the workbook here. Working in Google Sheets is fine, use File then
          Download then Microsoft Excel (.xlsx). Photos are matched to the{" "}
          <strong>Image File</strong> column, ignoring capitalisation. Put a
          consultation's capacity in <strong>Available Slots</strong> and a product's
          stock in <strong>Count in Stock</strong>. Rows marked out of stock are skipped,
          and anything already on this pharmacy is skipped too, so re-running the same
          workbook never creates duplicates.
        </>
      }
    />
  );
};

export default HealthcareImportModal;
